import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useInviteUserMutation } from "@/hooks/useUsers";
import type { UserRole } from "@/types/users";
import { ROLE_LABELS } from "@/types/users";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<UserRole>("installer");
  const [partnerId, setPartnerId] = useState<string>("");

  const inviteMutation = useInviteUserMutation();

  // Hae partnerit
  const { data: partners = [] } = useQuery({
    queryKey: ["partners-for-select"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partners")
        .select("id, name")
        .is("deleted_at", null)
        .order("name");

      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Reset form kun dialogi suljetaan
  useEffect(() => {
    if (!open) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setRole("installer");
      setPartnerId("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await inviteMutation.mutateAsync({
      email,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      role,
      partnerId: role === "partner" ? partnerId : undefined,
    });

    onOpenChange(false);
  };

  const isValid = email.includes("@") && (role !== "partner" || partnerId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Kutsu uusi käyttäjä</DialogTitle>
            <DialogDescription>
              Lähetä kutsulinkki sähköpostitse. Käyttäjä saa linkin salasanan asettamiseen.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Sähköposti *</Label>
              <Input
                id="email"
                type="email"
                placeholder="esimerkki@yritys.fi"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="firstName">Etunimi</Label>
                <Input
                  id="firstName"
                  placeholder="Matti"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="lastName">Sukunimi</Label>
                <Input
                  id="lastName"
                  placeholder="Meikäläinen"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="role">Rooli *</Label>
              <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {role === "partner" && (
              <div className="grid gap-2">
                <Label htmlFor="partner">Partner-organisaatio *</Label>
                <Select value={partnerId} onValueChange={setPartnerId}>
                  <SelectTrigger id="partner">
                    <SelectValue placeholder="Valitse partneri..." />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {partners.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Ei partnereita. Luo ensin partner Kumppanit-sivulla.
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Peruuta
            </Button>
            <Button type="submit" disabled={!isValid || inviteMutation.isPending}>
              {inviteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Lähetä kutsu
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
